import type { Strings } from "./i18n";
import type { OfferCard, PartnerMapContent } from "./types";

/** The amount in a price as written ("€36", "36,50 EUR"): its number, for comparing. */
const amount = (price: string): number => parseFloat(price.replace(/[^\d.,]/g, "").replace(",", "."));

/** The card's `from` as it writes it, else the lowest price in the last column of its price table ("€36"); null for a card with neither. The "from" of its photo and its lines. */
export function fromPrice(card: OfferCard): string | null {
	if (card.from) return card.from;
	const prices = card.prices?.rows.map((row) => row[row.length - 1]) ?? [];
	return prices.length ? prices.reduce((lowest, price) => (amount(price) < amount(lowest) ? price : lowest)) : null;
}

/** What stands beside a card's name in the base's sheet: its "from" price, else its score, else the `badge` the content gives it. */
export const sheetTag = (card: OfferCard, t: Strings): string => {
	const price = fromPrice(card);
	if (price) return t.fromPrice(price);
	return card.rating ? `${card.rating.score}★` : (card.badge ?? "");
};

/** how many offers the base's sheet lists: the sheet is a card over a map, not the tab */
export const SHEET_OFFERS = 4;

// One formatter for every render. en-CA writes a date as YYYY-MM-DD.
const AMSTERDAM_DAY = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Amsterdam", year: "numeric", month: "2-digit", day: "2-digit" });

/** The date in Amsterdam, YYYY-MM-DD: what an offer's `until` is measured against. The same on the server and in the browser, whatever their time zones. */
export const amsterdamDay = (now: Date = new Date()): string => AMSTERDAM_DAY.format(now);

/** An offer is shown through its `until` day. */
export const isLive = (card: OfferCard, day: string): boolean => card.until === undefined || day <= card.until;

/** The content as it is on `day`: without the offers that are over. The same object when none is, so that what hangs on it is not made again. */
export function liveContent(content: PartnerMapContent, day: string): PartnerMapContent {
	const { offers } = content;
	if (!offers || offers.cards.every((card) => isLive(card, day))) return content;
	return { ...content, offers: { ...offers, cards: offers.cards.filter((card) => isLive(card, day)) } };
}
